import React, { useState } from 'react';

import { calendarStrings } from '@data/calendar';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { iota } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';

const strings = createStrings({
  ja: {
    today: '今日',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: '4px 7px 0px 7px',
  },
  calendarTable: {
    ...common.middleText,
    ...common.textGray800,
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',

    gridAutoRows: '29px',
  },
  calendarHeader: {
    ...common.flexCenter,
    color: Color(theme.palette.primary.main).alpha(0.5).toString(),
  },
  innerCell: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    ...common.baseButton,
    ...common.flexCenter,
  },
  today: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
  },
  selected: {
    border: `1px solid ${theme.palette.primary.main}`,
  },
  selector: {
    ...common.smallerText,
    ...common.flexCenter,
    color: theme.palette.primary.main,
  },
  yearSelector: {
    gridColumnEnd: 3,
    gridColumnStart: 1,
  },
  monthSelector: {
    gridColumnEnd: 5,
    gridColumnStart: 3,
  },
  todaySelector: {
    ...common.baseButton,
    gridColumnStart: 7,
  },
  leftArrow: {
    marginRight: '7px',
    width: '5px',
    height: '5px',
    borderTop: `1px solid ${theme.palette.grey[500]}`,
    borderRight: `1px solid ${theme.palette.grey[500]}`,
    transform: 'rotate(225deg)',
  },
  rightArrow: {
    marginLeft: '7px',
    width: '5px',
    height: '5px',
    borderTop: `1px solid ${theme.palette.grey[500]}`,
    borderRight: `1px solid ${theme.palette.grey[500]}`,
    transform: 'rotate(45deg)',
  },
  clickable: {
    ...common.baseButton,
    padding: '5px',
  },
}));

export interface SmallCalendarProps {
  date: Date;
  onClick?: (date: Date) => void;
}

export const SmallCalendar: React.FC<SmallCalendarProps> = (props) => {
  const { date, onClick } = props;
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());

  const monthStart = dayjs(new Date(year, month)).startOf('month').startOf('day');
  const startDate = dayjs(monthStart).subtract((dayjs(monthStart).day() + 6) % 7, 'day');

  return (
    <div className={classes.container}>
      <div className={classes.calendarTable}>
        {iota(7).map((i) => (
          <div key={i} className={classes.calendarHeader}>
            {calendarStrings.weekdaysShort((i + 1) % 7)}
          </div>
        ))}
        {iota(42).map((i) => {
          const currentDate = startDate.add(i, 'day');
          const d = currentDate.get('date');
          const thisMonth = currentDate.get('month') === month;
          const today = currentDate.isSame(new Date(), 'date');
          const selected = currentDate.isSame(date, 'date');

          return (
            <div
              key={i}
              className={clsx(
                classes.flexCenter,
                !thisMonth && classes.textGray300,
                thisMonth && classes.textGray700,
              )}
            >
              <div
                className={clsx(
                  classes.innerCell,
                  today && classes.today,
                  selected && classes.selected,
                )}
                onClick={() => onClick?.(currentDate.toDate())}
              >
                {String(d)}
              </div>
            </div>
          );
        })}
        <div className={clsx(classes.selector, classes.yearSelector)}>
          <div
            className={classes.clickable}
            onClick={() => (year > 2022 ? setYear(year - 1) : null)}
          >
            <div className={classes.leftArrow}></div>
          </div>
          {year}年
          <div className={classes.clickable} onClick={() => setYear(year + 1)}>
            <div className={classes.rightArrow}></div>
          </div>
        </div>
        <div className={clsx(classes.selector, classes.monthSelector)}>
          <div
            className={classes.clickable}
            onClick={() => (month > 0 ? setMonth(month - 1) : null)}
          >
            <div className={classes.leftArrow}></div>
          </div>
          {month + 1}月
          <div
            className={classes.clickable}
            onClick={() => (month < 11 ? setMonth(month + 1) : null)}
          >
            <div className={classes.rightArrow}></div>
          </div>
        </div>
        <div
          className={clsx(classes.selector, classes.todaySelector)}
          onClick={() => {
            setYear(new Date().getFullYear());
            setMonth(new Date().getMonth());
            onClick?.(new Date());
          }}
        >
          {strings.today}
        </div>
      </div>
      <div className={classes.flexInline}></div>
    </div>
  );
};
